import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import config from 'config';
import withShopContext from 'core/hocs/withShopContext';
import withPageLayout from 'core/hocs/withPageLayout';
import { SortId, sortValues } from 'consts/sort';
import type { SearchWithFiltersRoute } from 'api/search/with-filters';
import { searchWithFiltersRoute } from 'apiRoutes/search';
import { serverRequests as pageLayoutServerRequests, GetPageLayoutResult } from '@layouts/PageLayout/PageLayout.utils';
import { getShopKey } from '@providers/ConfigProvider';
import EducationZonePage from '@pages/EducationZone';
import { trustedUsRequestConfig, GetTrustedUsVariables, GetTrustedUsResults } from 'graphql/queries/GetTrustedUs.query';
import { pageRequestConfig, GetPageResults, GetPageVariables } from 'graphql/queries/GetPageData.query';
import {
  testimonialRequestConfig,
  GetTestimonialsResult,
  GetTestimonialsVariables,
} from 'graphql/queries/GetTestimonials.query';
import {
  pageFilesRequestConfig,
  GetPagesFilesResults,
  GetPagesFilesVariables,
} from 'graphql/queries/GetPagesFiles.query';
import {
  articleCategoriesWithArticlesRequestConfig,
  GetArticleCategoriesWithArticlesResult,
  GetArticleCategoriesWithArticlesVariables,
} from 'graphql/queries/GetArticlesCategoriesWithArticles.query';
import graphQLRequest from 'utils/graphQLRequest';
import combineRequest, { RequestConfig } from 'utils/combineRequest';
import { startTransaction } from 'utils/startTransaction';
import { GetResponse } from 'utils/apiRoute';
import { serverApiFetch } from 'utils/fetch';
import handleError from 'utils/handleError';

const { routes } = config;

const serverRequests: RequestConfig[] = [
  ...pageLayoutServerRequests,
  testimonialRequestConfig,
  trustedUsRequestConfig,
  pageRequestConfig,
  pageFilesRequestConfig,
  articleCategoriesWithArticlesRequestConfig,
];

type Result = GetPageLayoutResult &
  GetTestimonialsResult &
  GetPageResults &
  GetPagesFilesResults &
  GetArticleCategoriesWithArticlesResult &
  GetTrustedUsResults & {
    searchData?: GetResponse<SearchWithFiltersRoute>;
  };

export const getServerSideProps: GetServerSideProps<Result> = withShopContext(async ({ res }, { client, shopId }) => {
  if (getShopKey(shopId) !== 'dellowo') {
    return handleError(res, 404);
  }

  const transaction = startTransaction({ op: 'server.requests', name: routes.educationZone.href });
  const combinedDataSpan = transaction.startChild({ op: 'combined-data' });

  const { data: combinedData } = await graphQLRequest<
    GetPageLayoutResult &
      GetTestimonialsResult &
      GetPageResults &
      GetPagesFilesResults &
      GetTrustedUsResults &
      GetArticleCategoriesWithArticlesResult,
    GetTestimonialsVariables &
      GetPageVariables &
      GetPagesFilesVariables &
      GetTrustedUsVariables &
      GetArticleCategoriesWithArticlesVariables
  >(client, combineRequest(serverRequests), {
    variables: {
      page: routes.educationZone.href,
      limit: 12,
      pages: [routes.educationZone.href],
      articleCategoriesSlug: { _eq: 'komputery-dla-szkol-strefa-wiedzy' },
      articleCategoriesLimit: 8,
    },
  });

  combinedDataSpan.finish();

  if (!combinedData) {
    transaction.finish();
    return handleError(res, 500);
  }

  const constantFilters = [{ key: 'tags', values: ['Laptop dla edukacji'] }];
  const { data: searchData } = await serverApiFetch(
    searchWithFiltersRoute,
    {
      name: '',
      mode: 'category',
      limit: 20,
      offset: 0,
      order: sortValues[SortId.Popularity],
      filters: constantFilters,
    },
    { headers: { 'x-commerce-shop-id': shopId } }
  );

  transaction.finish();

  return {
    props: { ...combinedData, searchData },
  };
});

const EducationZone = ({
  searchData,
  trustedUsLogos,
  articleCategories,
  testimonials,
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => (
  <EducationZonePage
    searchData={searchData}
    trustedUsLogos={trustedUsLogos}
    testimonials={testimonials}
    articleCategories={articleCategories}
  />
);

export default withPageLayout(EducationZone, {
  breadcrumbVariant: 'educationZone',
  showBreadcrumb: true,
  withoutBreadcrumbBorder: true,
});
